import React, { ReactNode, useEffect, useState } from 'react';

import { connectionErrors } from 'common/api/SsoApi';
import { SubmissionProps } from 'common/components/SignForm';
import SignIn from 'common/components/SignInForm/Component';
import TermsOfServiceForm from 'common/components/TermsOfServiceForm';
import {
  BRAND_HAS_TOS_VALIDATION_WITH_GOOGLE,
  BRAND_HAS_FB_CONNECT,
  BRAND_HAS_REWIND_OPE
} from 'common/configuration/constants';
import { IdentityProviderTypes } from 'common/constants/IdentityProvidersTypes';
import { connect } from 'common/module/social-network-connect-buttons';
import emailExist from 'common/services/emailExist';
import debounce from 'common/tools/functions/debounce';
import request, { RequestError } from 'common/tools/network/request';
import { path } from 'common/tools/network/routing';
import titleCase from 'common/tools/string/titleCase';
import trans from 'common/tools/translations/trans';
import { hit as hit_jan } from 'common/tracking/jan';

import SocialSignButtons, {
  CredentialsToSubmit
} from '../socialNetworks/SocialSignButtons';

type SignInFormProps = {
  redirectUrl?: string;
  termsPolicyOnSignIn: boolean;
};

enum STEPS {
  SIGN_IN = 'sign-in',
  TERMS_OF_SERVICES = 'terms-of-service'
}

const serverErrorMessage = trans('userspace.sign-up.server-error.text');
const loginError = trans('account.login-error');
const title = BRAND_HAS_REWIND_OPE
  ? trans('userspace.sign-in.title-rewind')
  : trans('userspace.sign-in.title');

const doTracking = (provider: IdentityProviderTypes | null = null) => {
  let label = 'email';
  if (provider) {
    if (provider === IdentityProviderTypes.GOOGLE) {
      label = 'sso_google';
    } else {
      label = 'undefined';
    }
  }

  hit_jan('signin', {
    account_from: label
  });
};

const SignInForm = ({ redirectUrl }: SignInFormProps) => {
  const [credentials, setCredentials] = useState<CredentialsToSubmit | null>(
    null
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [provider, setProvider] = useState('');
  const [socialErrorMessages, setSocialErrorMessages] = useState<string[]>([]);
  const [step, setStep] = useState(STEPS.SIGN_IN);
  const [submitting, setSubmitting] = useState(false);

  const resetErrors = () => {
    setErrorMessages([]);
    setSocialErrorMessages([]);
  };

  const reset = () => {
    setCredentials(null);
    setSubmitting(false);
    resetErrors();
  };

  const goToSignInForm = () => {
    reset();
    setStep(STEPS.SIGN_IN);
  };

  const getErrorMessage = (e: unknown, defaultMessage: string) => {
    let errorMessage = defaultMessage;
    if (e instanceof RequestError && e.data) {
      if (e.data.code === connectionErrors.ACCOUNT_EXISTS) {
        errorMessage = trans('userspace.sign-up.server-error.account-exists', {
          provider: titleCase(provider)
        });
      } else if (e.data.code === connectionErrors.SOCIAL_NO_MAIL) {
        errorMessage = trans('userspace.sign-up.server-error.empty-email', {
          provider: titleCase(provider)
        });
      }
    }
    return errorMessage;
  };

  const getSocialError = (e?: unknown) => {
    const socialErrors = getErrorMessage(e, serverErrorMessage);
    setSocialErrorMessages([socialErrors]);
  };

  const getSubmitError = (e: unknown) => {
    const errors = getErrorMessage(e, loginError);
    setErrorMessages([errors]);
  };

  const handleSocialConnect = async (
    accountExist: boolean,
    { name, provider, termsOfService, token }: CredentialsToSubmit
  ) => {
    setSubmitting(true);
    setProvider(provider);
    doTracking(provider);
    try {
      await connect(
        provider,
        // In the case of a connection, only the token is
        // valid, otherwise SSO will reject the connection
        // attempt.
        accountExist || !BRAND_HAS_TOS_VALIDATION_WITH_GOOGLE
          ? { token }
          : {
              token,
              profile: { nickname: name, cguValidation: termsOfService }
            }
      );
    } catch (e) {
      setSubmitting(false);
      getSocialError(e);
    }
  };

  const handleTOSSubmit = async (
    email: string,
    nickname: string,
    termsOfService?: boolean
  ) => {
    resetErrors();
    // Currently, login APIs (Google, Facebook) issue a
    // login token associated with an email. If the email is
    // changed by the user, the token becomes invalid.
    if (!credentials || email !== credentials.email) {
      getSocialError();
    } else {
      const newCredentials = {
        ...credentials,
        name: nickname,
        termsOfService
      };
      setCredentials(newCredentials);
    }
  };

  const handleSubmit = async ({
    email,
    password,
    captchaToken
  }: SubmissionProps) => {
    doTracking();
    resetErrors();
    setSubmitting(true);
    const postData = {
      'g-recaptcha-response': captchaToken,
      'sign_in[_password]': password,
      'sign_in[_redirect_url]': redirectUrl,
      'sign_in[_username]': email
    };

    try {
      const response = await request(path('signin'), {
        method: 'POST',
        payload: postData
      });
      if (response.redirect_url) {
        // We delay reloading to give third-party APIs time to perform their
        // asynchronous tasks (for example, sending tracking data with JAN).
        debounce(() => {
          window.location.href = response.redirect_url;
        })();
      } else {
        setSubmitting(false);
      }
    } catch (e) {
      setSubmitting(false);
      getSubmitError(e);
    }
  };

  // Triggering the validation of an account
  // registration via a social provider (Google or
  // Facebook)
  useEffect(() => {
    if (credentials) {
      emailExist(credentials.email)
        .then(accountExist => {
          // Either the user is logging in with an existing Google
          // or Facebook account, or the user has tried to log in
          // with a new Google or Facebook account and has
          // just validated the terms of service.
          if (
            accountExist ||
            !BRAND_HAS_TOS_VALIDATION_WITH_GOOGLE ||
            step === STEPS.TERMS_OF_SERVICES
          ) {
            handleSocialConnect(accountExist, credentials);
          } else {
            // go to TERMS_OF_SERVICES Form
            setStep(STEPS.TERMS_OF_SERVICES);
          }
        })
        .catch(e => {
          setSubmitting(false);
          getSubmitError(e);
        });
    }
  }, [credentials]);

  const handleSocialClick = (credentials: CredentialsToSubmit) => {
    resetErrors();
    setCredentials(credentials);
  };

  let content: ReactNode;
  if (step === STEPS.TERMS_OF_SERVICES) {
    content = (
      <TermsOfServiceForm
        displayEmail
        email={credentials?.email ?? ''}
        errorMessages={socialErrorMessages}
        nickname={credentials?.name ?? ''}
        onReturn={goToSignInForm}
        onSubmit={handleTOSSubmit}
        submitting={submitting}
      />
    );
  } else {
    content = (
      <>
        <h1 className="title">{title}</h1>
        {BRAND_HAS_REWIND_OPE && <div className="rewind-signin-logo"></div>}
        <SocialSignButtons
          errorMessages={socialErrorMessages}
          onSocialClick={handleSocialClick}
          submitting={submitting}
          fbButton
          fbModal={!BRAND_HAS_FB_CONNECT}
        />
        <SignIn
          errorMessages={errorMessages}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
        <div className="bottom-link">
          {`${trans('userspace.header.register_label')} `}
          <a href={path('signup', { redirectUrl })}>
            {trans('userspace.header.register')}
          </a>
        </div>
      </>
    );
  }

  return content;
};

export default SignInForm;
